import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "499",
  "slug": "how-to-read-a-climate-graph-climograph",
  "title": "How to Read a Climate Graph (Climograph)",
  "excerpt": "A climograph squeezes a place’s whole year of weather into one chart. Here is how to read the bars and the line, and what they reveal about a location.",
  "date": "2026-06-22",
  "category": "Education",
  "image": "https://images.unsplash.com/photo-1501630834273-4b5604d2ee31?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHx3ZWF0aGVyJTIwY2xvdWRzJTIwc2t5fGVufDF8MHx8fDE3ODIxMTYwNDN8MA&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "5 min",
  "author": "EarthGuessr Team",
  "tldr": "A climograph shows a location’s average monthly temperature as a line and its average monthly rainfall as bars, across the twelve months of a typical year. Reading the seasonality of the two together tells you the climate type — and even which hemisphere the place is in.",
  "content": [
    {
      "type": "paragraph",
      "text": "Ask what the weather is like somewhere and you will get a vague answer. Ask to see its climograph and you get the whole story on a single chart. A climograph, or climate graph, distils years of weather records into one tidy picture of a typical year — and once you know how to read it, you can work out an astonishing amount about a place you have never been."
    },
    {
      "type": "heading",
      "text": "The Two Halves of a Climograph"
    },
    {
      "type": "paragraph",
      "text": "Every climograph combines two datasets on the same chart, sharing a horizontal axis marked with the twelve months of the year, usually January to December."
    },
    {
      "type": "list",
      "items": [
        "Temperature: shown as a line, plotted against a temperature scale on one side of the chart. Each point is the average temperature for that month.",
        "Precipitation: shown as bars, plotted against a rainfall scale on the other side. Each bar is the average rainfall for that month, usually in millimetres."
      ]
    },
    {
      "type": "paragraph",
      "text": "Because temperature and rainfall use different units, a climograph has two vertical axes — one on the left and one on the right. The first thing to do with any climograph is check which axis goes with the line and which goes with the bars, so you do not misread the numbers."
    },
    {
      "type": "heading",
      "text": "Step One: Read the Temperature Line"
    },
    {
      "type": "paragraph",
      "text": "Trace the line across the year. Is it nearly flat, or does it rise and fall sharply between summer and winter? A flat line means little seasonal change, typical of the tropics. A line with a steep peak and trough means strong seasons, typical of places far from the equator or deep inside continents. The height of the line tells you whether the place is generally hot, mild, or cold, and the gap between the highest and lowest points gives the annual temperature range."
    },
    {
      "type": "heading",
      "text": "Step Two: Read the Rainfall Bars"
    },
    {
      "type": "paragraph",
      "text": "Now look at the bars. Add up roughly how much rain falls over the year — tall bars throughout mean a wet climate, short bars mean a dry one. Just as important is when the rain falls. Bars that are even across the year indicate rain in all seasons. Bars bunched into a few months indicate a distinct wet season and dry season, the hallmark of monsoon and savanna climates. A dip in rainfall during the warmest months is the signature of a Mediterranean climate."
    },
    {
      "type": "heading",
      "text": "Step Three: Put Them Together"
    },
    {
      "type": "paragraph",
      "text": "The real skill is reading the line and bars together, because the combination identifies the climate type:"
    },
    {
      "type": "list",
      "items": [
        "Warm and wet all year, with a flat temperature line — a tropical rainforest climate.",
        "Hot with a sharp wet season and a long dry season — a tropical savanna or monsoon climate.",
        "Hot and very dry all year, with almost no bars — a desert climate.",
        "Warm, dry summers and mild, wetter winters — a Mediterranean climate.",
        "Big temperature swings with rain spread through the year — a continental climate."
      ]
    },
    {
      "type": "heading",
      "text": "A Clever Trick: Which Hemisphere?"
    },
    {
      "type": "paragraph",
      "text": "Here is a detail that surprises people. Because the seasons are reversed north and south of the equator, a climograph can reveal which hemisphere a place is in. If the temperature line peaks in June, July, and August, the warmest months are mid-year, so the location is in the Northern Hemisphere. If it peaks in December, January, and February, summer falls at the turn of the year, placing it in the Southern Hemisphere. One glance at the shape of the line, and you have narrowed down half the planet."
    },
    {
      "type": "heading",
      "text": "Why Climographs Are Useful"
    },
    {
      "type": "paragraph",
      "text": "Climographs are a staple of geography classrooms because they pack so much into so little space. They let you compare two places at a glance, classify climates using systems like the Köppen scheme, and understand why certain crops, plants, and ways of life suit certain regions. They turn raw weather records into a shape your eye can grasp instantly."
    },
    {
      "type": "paragraph",
      "text": "Best of all, the skill transfers. Once you can read a place’s climate from a chart, you start noticing the same clues in the landscape itself — the lush green of constant rain, the sparse cover of a dry season, the snow line of a cold winter. That instinct for matching climate to scenery is exactly what sharpens your guesses in EarthGuessr, where reading the environment is half the game."
    }
  ]
};
